import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, writeBatch } from "firebase/firestore";
import { auth, db, googleProvider } from "../config/firebase";
import { User, AuthState, UserRole, SubscriptionTier } from "../types";
import { RoleService } from "../services/roleService";
import { isEmailDomainAllowed, getDomainError } from "../config/allowedDomains";
import {
  validateUsername,
  cleanObjectForFirestore,
} from "../utils/usernameValidation";
import { generateTemporaryCredentials } from "../utils/nameGenerator";

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  completeGoogleSignup: (
    username: string,
    displayName: string
  ) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    username: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (updates: Partial<User>) => Promise<void>;
  refreshUserMapsCount: () => Promise<void>;
  refreshUser: () => Promise<void>;
  signupSuccess: boolean;
  resetSignupSuccess: () => void;

  // Google signup state
  googleSignupUser: any | null;
  showUsernameSelection: boolean;

  // Role-based methods
  canCreateMap: () => boolean;
  canCreateOverlay: () => boolean;
  canCreatePublicMaps: () => boolean;
  canApproveMaps: () => boolean;
  canAccessAdminPanel: () => boolean;
  canAccessApproverPanel: () => boolean;
  isAdmin: () => boolean;
  isApprover: () => boolean;
  getRemainingMapSlots: () => number;
  getRemainingOverlaySlots: () => number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [googleSignupUser, setGoogleSignupUser] = useState<any | null>(null);
  const [showUsernameSelection, setShowUsernameSelection] = useState(false);
  const isCreatingTemporaryUserRef = useRef(false);
  const isCompletingSignupRef = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Skip Firestore lookup if we're in the middle of creating a temporary user
        if (isCreatingTemporaryUserRef.current) {
          return;
        }

        // Skip Firestore lookup if we're completing signup to prevent race conditions
        if (isCompletingSignupRef.current) {
          return;
        }

        try {
          setLoading(true);
          setError(null); // Clear any previous errors

          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data() as User;

            // Simply set the user data from Firestore
            setUser(userData);
            setError(null); // Clear any errors on successful sign in
            setShowUsernameSelection(false); // User has completed setup
            setGoogleSignupUser(null); // Clear any pending Google signup
          } else {
            // New Google user - they need to complete setup

            // Create a temporary user account for the tour
            const tempCredentials = generateTemporaryCredentials();

            const tempUserData: User = {
              id: firebaseUser.uid,
              username: tempCredentials.username,
              email: firebaseUser.email || "",
              displayName: tempCredentials.displayName,
              createdAt: new Date().toISOString(),
              mapsCount: 0,
              followersCount: 0,
              followingCount: 0,
              role: "user" as UserRole,
              isApproved: true,
              subscriptionTier: "free" as SubscriptionTier,
              emailVerified: true,
              usernameLastChangedAt: new Date().toISOString(),
              previousUsernames: [],
            };

            // Create temporary user document and username reservation
            const batch = writeBatch(db);
            const cleanUserData = cleanObjectForFirestore(tempUserData);

            batch.set(doc(db, "users", firebaseUser.uid), cleanUserData);
            batch.set(
              doc(db, "usernames", tempCredentials.username.toLowerCase()),
              {
                uid: firebaseUser.uid,
                username: tempCredentials.username,
                createdAt: new Date().toISOString(),
              }
            );

            await batch.commit();

            // Set up the tour
            setGoogleSignupUser(firebaseUser);
            setShowUsernameSelection(true);
            setUser(tempUserData);
          }
        } catch (err: any) {
          console.error("onAuthStateChanged error:", err);
          setError(
            "Unable to load your account information. Please try again."
          );
          setUser(null);
        } finally {
          setLoading(false);
        }
      } else {
        // Only reset user state if there's no user
        setUser(null);
        setError(null); // Clear any errors when user signs out
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      const { user: firebaseUser } = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Wait for user data to be loaded from Firestore

      const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data() as User;

        setUser(userData);
      } else {
        console.error("User document not found for UID:", firebaseUser.uid);
        throw new Error(
          "Your account information could not be found. Please contact support."
        );
      }
    } catch (err: any) {
      console.error("Sign in error", err);

      // Provide user-friendly error messages
      let errorMessage = "Unable to sign in at this time";

      if (err.code === "auth/user-not-found") {
        errorMessage =
          "No account found with this email. Please check your email or create a new account.";
      } else if (err.code === "auth/wrong-password") {
        errorMessage = "Incorrect password. Please try again.";
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (
        err.message &&
        !err.message.includes("Firebase") &&
        !err.message.includes("auth/")
      ) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Set flag immediately to prevent onAuthStateChanged interference
      isCreatingTemporaryUserRef.current = true;

      setError(null);
      setLoading(true);

      const { user: firebaseUser } = await signInWithPopup(
        auth,
        googleProvider
      );

      // Check if user already exists in Firestore
      const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));

      if (userDoc.exists()) {
        // User exists, just sign them in
        const userData = userDoc.data() as User;
        setUser(userData);
      } else {
        // Clear any existing errors immediately
        setError(null);

        // Create a temporary user document with memorable placeholder names
        // This prevents the "data not found" error while allowing username selection
        const tempCredentials = generateTemporaryCredentials();

        const tempUserData: User = {
          id: firebaseUser.uid,
          username: tempCredentials.username,
          email: firebaseUser.email || "",
          displayName: tempCredentials.displayName, // Memorable placeholder display name
          // avatar field omitted - user can add one later if desired
          createdAt: new Date().toISOString(),
          mapsCount: 0,
          followersCount: 0,
          followingCount: 0,
          role: "user" as UserRole,
          isApproved: true,
          subscriptionTier: "free" as SubscriptionTier,
          emailVerified: true,
          usernameLastChangedAt: new Date().toISOString(),
          previousUsernames: [],
        };

        // Create temporary user document and username reservation
        const batch = writeBatch(db);

        // Create a clean user document without undefined fields
        const cleanUserData = cleanObjectForFirestore(tempUserData);

        batch.set(doc(db, "users", firebaseUser.uid), cleanUserData);
        batch.set(
          doc(db, "usernames", tempCredentials.username.toLowerCase()),
          {
            uid: firebaseUser.uid,
            username: tempCredentials.username,
            createdAt: new Date().toISOString(),
          }
        );

        await batch.commit();

        // Store Google user info for username selection
        setGoogleSignupUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email || "",
          displayName: firebaseUser.displayName || "",
          photoURL: firebaseUser.photoURL || undefined,
        });
        setShowUsernameSelection(true);

        // Set the temporary user so dashboard can load
        // Use the original tempUserData since it's guaranteed to have all required fields
        setUser(tempUserData);

        // Immediately clear any errors when user is set
        setError(null);

        // Reset the flag since temporary user creation is complete
        isCreatingTemporaryUserRef.current = false;

        setLoading(false);
        return;
      }
    } catch (err: any) {
      console.error("🔧 Google sign in error", err);
      console.error("🔧 Error details:", err.message, err.code);

      // Handle specific popup cancellation error
      if (
        err.code === "auth/cancelled-popup-request" ||
        err.code === "auth/popup-closed-by-user"
      ) {
        setError(null); // Clear any previous errors
        // Don't show error message for user cancellation
      } else {
        setError("Unable to sign in with Google. Please try again.");
      }

      // Reset the flag on error
      isCreatingTemporaryUserRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const completeGoogleSignup = async (
    username: string,
    displayName: string
  ) => {
    if (!googleSignupUser) return;

    try {
      setLoading(true);
      setError(null);
      isCompletingSignupRef.current = true;

      // Validate username using centralized validation
      const validation = validateUsername(username);
      if (!validation.isValid) {
        throw new Error(validation.error || "Invalid username");
      }

      // Check if username is available
      const usernameDoc = await getDoc(
        doc(db, "usernames", username.toLowerCase())
      );
      if (usernameDoc.exists()) {
        throw new Error(
          "This username is already taken. Please choose a different one."
        );
      }

      // Update the existing user document with the chosen username
      const batch = writeBatch(db);

      // Remove old temporary username reservation
      const currentUser = user; // Get current user from state
      if (currentUser && currentUser.username.startsWith("temp_")) {
        batch.delete(doc(db, "usernames", currentUser.username.toLowerCase()));
      }

      // Add new username reservation
      batch.set(doc(db, "usernames", username.toLowerCase()), {
        uid: googleSignupUser.uid,
        username: username,
        createdAt: new Date().toISOString(),
      });

      // Update user document with new username and display name
      const updatedUserData: User = {
        ...currentUser!,
        username: username,
        displayName: displayName,
        usernameLastChangedAt: new Date().toISOString(),
      };

      // Only update the fields we want to change, avoiding undefined values
      const updateData: Partial<User> = {
        username: username,
        displayName: displayName,
        usernameLastChangedAt: new Date().toISOString(),
      };

      // Use the current user's ID, not googleSignupUser.uid
      batch.update(doc(db, "users", currentUser!.id), updateData);

      await batch.commit();
      setUser(updatedUserData);
      // Don't set signupSuccess for Google users - they've already completed signup
      setShowUsernameSelection(false);
      setGoogleSignupUser(null);
      isCompletingSignupRef.current = false;
    } catch (err: any) {
      console.error("Error completing Google signup:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
      isCompletingSignupRef.current = false;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    displayName: string,
    username: string
  ) => {
    let firebaseUser: any = null;
    let userCreated = false;
    let userData: User | null = null;

    try {
      setError(null);

      // Validate username using centralized validation
      const validation = validateUsername(username);
      if (!validation.isValid) {
        throw new Error(validation.error || "Invalid username");
      }

      // Validate email format
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new Error("Please enter a valid email address.");
      }

      // Validate password
      if (!password || password.length < 6) {
        throw new Error("Password must be at least 6 characters long.");
      }

      // Validate display name
      if (!displayName || displayName.trim().length < 2) {
        throw new Error("Display name must be at least 2 characters long.");
      }

      // Check if username is already taken (case-insensitive)
      const usernameDoc = await getDoc(
        doc(db, "usernames", username.toLowerCase())
      );
      if (usernameDoc.exists()) {
        throw new Error(
          "This username is already taken. Please choose a different one."
        );
      }

      // Check if user already exists in Firestore
      const existingUserDoc = await getDoc(doc(db, "users", email));
      if (existingUserDoc.exists()) {
        const existingUser = existingUserDoc.data() as User;

        // If email exists and is verified, block new signup
        if (existingUser.emailVerified) {
          throw new Error("A user with this email already exists");
        } else {
          // Email exists but is not verified - we could either:
          // 1. Allow new signup (current approach)
          // 2. Send verification email to existing account
          // 3. Block signup and ask user to verify existing account

          // Allow new signup for now
        }
      }

      // Check if email domain is allowed
      if (!isEmailDomainAllowed(email)) {
        throw new Error(getDomainError(email));
      }

      // Create Firebase Auth user
      const { user: newFirebaseUser } = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      firebaseUser = newFirebaseUser;
      userCreated = true;

      // Update Firebase Auth profile
      await updateProfile(firebaseUser, { displayName });

      // Create user document in Firestore
      userData = {
        id: firebaseUser.uid,
        username,
        email,
        displayName,
        createdAt: new Date().toISOString(),
        mapsCount: 0,
        followersCount: 0,
        followingCount: 0,
        role: "user" as UserRole,
        isApproved: true, // Users are approved by default, only maps need approval
        subscriptionTier: "free" as SubscriptionTier,
        emailVerified: true, // New users are verified by default

        usernameLastChangedAt: new Date().toISOString(), // Initialize username change tracking
        previousUsernames: [], // Initialize empty previous usernames array
      };

      // Create user document and username reservation atomically using batch write
      const batch = writeBatch(db);

      // Add user document
      batch.set(doc(db, "users", firebaseUser.uid), userData);

      // Reserve username (case-insensitive)
      batch.set(doc(db, "usernames", username.toLowerCase()), {
        uid: firebaseUser.uid,
        username: username,
        createdAt: new Date().toISOString(),
      });

      // Commit the batch
      try {
        await batch.commit();
      } catch (batchError: any) {
        console.error("Failed to save user data:", batchError);
        throw new Error("Failed to save user information. Please try again.");
      }

      if (userData) {
        setUser(userData);
        setSignupSuccess(true);
        setError(null);
      }
    } catch (err: any) {
      console.error("Signup error:", err);

      // Clean up if Firebase Auth user was created but something else failed
      if (userCreated && firebaseUser) {
        try {
          // Delete the Firebase Auth user if Firestore creation failed
          await firebaseUser.delete();
        } catch (cleanupErr) {
          console.error("Failed to cleanup user account:", cleanupErr);
        }
      }

      // Set a more user-friendly error message
      let errorMessage = "Unable to create your account at this time";

      if (err.code === "auth/email-already-in-use") {
        errorMessage =
          "An account with this email already exists. Please try signing in instead.";
      } else if (err.code === "auth/weak-password") {
        errorMessage =
          "Password is too weak. Please use at least 6 characters.";
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (err.message) {
        // Check for specific error messages first
        if (err.message.includes("username is already taken")) {
          errorMessage =
            "This username is already taken. Please choose a different one.";
        } else if (
          err.message.includes("user with this email already exists")
        ) {
          errorMessage =
            "An account with this email already exists. Please try signing in instead.";
        } else if (err.message.includes("domain not allowed")) {
          errorMessage = err.message;
        } else if (err.message.includes("Failed to save user information")) {
          errorMessage = "Failed to save user information. Please try again.";
        } else if (err.message.includes("Password must be at least")) {
          errorMessage = err.message;
        } else if (err.message.includes("Display name must be at least")) {
          errorMessage = err.message;
        } else if (err.message.includes("Username must be at least")) {
          errorMessage = err.message;
        } else if (err.message.includes("Please enter a valid email")) {
          errorMessage = err.message;
        } else {
          // For Firebase errors, provide a generic message but log the actual error
          console.error("Firebase signup error:", err);
          errorMessage =
            "Unable to create account at this time. Please try again later.";
        }
      } else if (err.code) {
        // Handle other Firebase error codes
        console.error("Firebase error code:", err.code);
        errorMessage =
          "Unable to create account at this time. Please try again later.";
      }

      setError(errorMessage);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const updateUserProfile = async (updates: Partial<User>) => {
    if (!user) return;

    try {
      // Check if username is being changed
      if (updates.username && updates.username !== user.username) {
        // Validate username format
        if (!updates.username || updates.username.length < 3) {
          throw new Error("Username must be at least 3 characters long.");
        }

        if (updates.username.length > 20) {
          throw new Error("Username must be 20 characters or less.");
        }

        if (!/^[a-zA-Z0-9_-]+$/.test(updates.username)) {
          throw new Error(
            "Username can only contain letters, numbers, underscores, and hyphens."
          );
        }

        // Check 3-day cooldown for username changes
        if (user.usernameLastChangedAt) {
          const lastChangeDate = new Date(user.usernameLastChangedAt);
          const threeDaysAgo = new Date();
          threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

          if (lastChangeDate > threeDaysAgo) {
            const daysRemaining = Math.ceil(
              (lastChangeDate.getTime() - threeDaysAgo.getTime()) /
                (1000 * 60 * 60 * 24)
            );
            throw new Error(
              `Username can only be changed once every 3 days. You can change it again in ${daysRemaining} day${
                daysRemaining !== 1 ? "s" : ""
              }.`
            );
          }
        }

        // Check if username is already taken (case-insensitive)
        const usernameDoc = await getDoc(
          doc(db, "usernames", updates.username.toLowerCase())
        );
        if (usernameDoc.exists()) {
          const existingUser = usernameDoc.data();
          // Allow if it's the same user (case change) or if it's a different user
          if (existingUser.uid !== user.id) {
            throw new Error(
              "This username is already taken. Please choose a different one."
            );
          }
        }

        // Username is valid and available, proceed with update
        const batch = writeBatch(db);

        // Remove old username reservation
        batch.delete(doc(db, "usernames", user.username.toLowerCase()));

        // Add new username reservation
        batch.set(doc(db, "usernames", updates.username.toLowerCase()), {
          uid: user.id,
          username: updates.username,
          createdAt: new Date().toISOString(),
        });

        // Update user document with new username and tracking fields
        const updatedUser = {
          ...user,
          ...updates,
          usernameLastChangedAt: new Date().toISOString(),
          previousUsernames: [
            ...(user.previousUsernames || []),
            user.username,
          ].slice(-5), // Keep only last 5 usernames
        };

        // Filter out undefined values before saving to Firestore
        const cleanUpdatedUser = cleanObjectForFirestore(updatedUser);

        batch.set(doc(db, "users", user.id), cleanUpdatedUser);

        // Commit all changes atomically
        await batch.commit();
        setUser(updatedUser);
      } else {
        // No username change, just update other fields
        const updatedUser = { ...user, ...updates };

        // Filter out undefined values before saving to Firestore
        const cleanUpdatedUser = cleanObjectForFirestore(updatedUser);

        await setDoc(doc(db, "users", user.id), cleanUpdatedUser);
        setUser(updatedUser);
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  // Refresh user's maps count from database
  const refreshUserMapsCount = async () => {
    if (!user) return;

    try {
      const userDoc = await getDoc(doc(db, "users", user.id));
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        setUser({ ...user, mapsCount: userData.mapsCount });
      }
    } catch (error) {
      console.error("Error refreshing user maps count:", error);
    }
  };

  const refreshUser = async () => {
    if (!user) return;

    try {
      const userDoc = await getDoc(doc(db, "users", user.id));
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        setUser(userData);

        // Check if user has completed setup by looking at usernameLastChangedAt
        // If this field exists, it means the user has completed the username setup process
        if (userData.usernameLastChangedAt) {
          setShowUsernameSelection(false);
        }
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  // Reset signup success state
  const resetSignupSuccess = () => {
    setSignupSuccess(false);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    signIn,
    signInWithGoogle,
    completeGoogleSignup,
    signUp,
    logout,
    updateUserProfile,
    refreshUserMapsCount,
    refreshUser,
    signupSuccess,
    resetSignupSuccess,
    googleSignupUser,
    showUsernameSelection,

    // Role-based methods
    canCreateMap: () => {
      if (!user) return false;
      return RoleService.canCreateMap(
        user.role,
        user.mapsCount,
        user.subscriptionTier
      );
    },
    canCreateOverlay: () => {
      if (!user) return false;
      // For now, we'll use mapsCount as overlay count until we add overlay tracking
      return RoleService.canCreateOverlay(
        user.role,
        user.mapsCount,
        user.subscriptionTier
      );
    },
    canCreatePublicMaps: () => {
      if (!user) return false;
      return RoleService.canCreatePublicMaps(user.role);
    },
    canApproveMaps: () => {
      if (!user) return false;
      return RoleService.canApproveMaps(user.role);
    },
    canAccessAdminPanel: () => {
      if (!user) return false;
      return RoleService.canAccessAdminPanel(user.role);
    },
    canAccessApproverPanel: () => {
      if (!user) return false;
      return RoleService.canAccessApproverPanel(user.role);
    },
    isAdmin: () => {
      if (!user) return false;
      return RoleService.isAdmin(user.role);
    },
    isApprover: () => {
      if (!user) return false;
      return RoleService.isApprover(user.role);
    },
    getRemainingMapSlots: () => {
      if (!user) return 0;
      return RoleService.getRemainingMapSlots(
        user.role,
        user.mapsCount,
        user.subscriptionTier
      );
    },
    getRemainingOverlaySlots: () => {
      if (!user) return 0;
      // For now, we'll use mapsCount as overlay count until we add overlay tracking
      return RoleService.getRemainingOverlaySlots(
        user.role,
        user.mapsCount,
        user.subscriptionTier
      );
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
